import Hls from 'hls.js/dist/hls.js'
// eslint-disable-next-line import/no-cycle
import HTML5Player from '../HTML5Player'
import { PLAYER_ERROR_CODES, PLAYER_REPORTABLE_EVENTS } from '../constants'

export default class HLSPBSPlayer {
  _hls = null
  _playerRef = null
  _contentUrl = null
  _setCaptionsAvailable

  constructor(
    playerRef,
    { contentSourceUrl, setCaptionsAvailable, reportPlayerEvent },
    customHlsConfig = {}
  ) {
    this._playerRef = playerRef
    this._setCaptionsAvailable = setCaptionsAvailable
    this._contentUrl = contentSourceUrl
    this._hadNetworkError = false
    const defaultHlsConfig = {
      enableWorker: false,
      backBufferLength: 15,
      capLevelToPlayerSize: true,
      ignoreDevicePixelRatio: true,
      maxBufferSize: 20 * 1000 * 1000,
      fragLoadPolicy: {
        default: {
          maxLoadTimeMs: 30 * 1000,
          maxTimeToFirstByteMs: 30 * 1000,
          errorRetry: {
            maxNumRetry: 2,
            retryDelayMs: 1000,
            maxRetryDelayMs: 8000,
          },
          timeoutRetry: {
            maxNumRetry: 3,
            maxRetryDelayMs: 0,
            retryDelayMs: 0,
          },
        },
      },
    }

    const hlsConfig = { ...defaultHlsConfig, ...customHlsConfig }
    this._hls = new Hls(hlsConfig)
    this._hlsConfig = hlsConfig
    this._initializeListeners()
    this.isInitialized = false
    this._reportEvent = reportPlayerEvent
  }

  _initializeListeners() {
    this._hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (this._contentUrl) {
        this._hls.loadSource(this._contentUrl)
      }
    })
    // This is the very next event that HLS emits after setting up text tracks.
    // The CUES_PARSED event does not work with our setup.
    this._hls.on(Hls.Events.SUBTITLE_FRAG_PROCESSED, () => {
      this._setCaptionsAvailable(true)
    })
    this._hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.play()
    })
    this._hls.on(Hls.Events.ERROR, (_, data) => {
      if (
        [
          Hls.ErrorDetails.FRAG_LOAD_TIMEOUT,
          Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT,
        ].includes(data.details)
      ) {
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.REBUFFER_START)
      } else if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.BUFFER_STALLED)
      }
      if (data.type === 'networkError' && data.fatal) {
        this._hadNetworkError = true

        const errorParams = {
          code: PLAYER_ERROR_CODES.NETWORK,
          message: 'Hls failed to load due to network error',
          data,
        }

        if ([403, 451].includes(data?.response?.code)) {
          errorParams.code = PLAYER_ERROR_CODES.GEO
          errorParams.message = 'Hls failed to load due to geo restriction'
          errorParams.data = { ...errorParams.data, type: data?.response?.code }
        }

        this._reportEvent('error', errorParams)
      } else if (data.type === 'mediaError' && data.fatal) {
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.ERROR, {
          code: PLAYER_ERROR_CODES.MEDIA,
          message: 'hls.js fatal mediaError',
          data: data.details,
        })
      } else if (data.fatal) {
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.ERROR, {
          code: PLAYER_ERROR_CODES.GENERIC_ERROR,
          message: 'Hls failed to load due to unknown error',
          data: data.details,
        })
      }
    })
    this._hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
      // console.log('Level switched', {
      //   codecSet: this._hls.levels[data.level].codecSet,
      //   height: this._hls.levels[data.level].height,
      //   width: this._hls.levels[data.level].width,
      //   videoCodec: this._hls.levels[data.level].videoCodec,
      // })
      const currentLevel = this._hls.levels[data.level]
      const { bitrate, width, height } = currentLevel
      this._reportEvent(PLAYER_REPORTABLE_EVENTS.RENDITION_CHANGE, {
        bitrate,
        width,
        height,
      })
    })

    // Listen for HTML5 'waiting' event to detect buffer depletion
    // This fires when playback is blocked due to lack of data
    this._playerRef.current?.addEventListener('waiting', () => {
      // Only report buffer depletion if we had a network error
      // This prevents normal startup buffering from triggering error handling
      if (this._hadNetworkError) {
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.BUFFER_DEPLETED)
      }
    })
  }

  init() {
    this._hls.attachMedia(this._playerRef.current)
    this.isInitialized = true
  }

  load(source) {
    try {
      if (this.isInitialized === true) {
        this._hls.destroy()
        this._hls = new Hls(this._hlsConfig)
        this._initializeListeners()
        this.init()
      }
      this._hls.loadSource(source)
    } catch (error) {
      this._reportEvent(PLAYER_REPORTABLE_EVENTS.ERROR, {
        code: PLAYER_ERROR_CODES.MEDIA,
        message: 'hls.js fatal mediaError',
        data: error,
      })
    }
  }

  async play() {
    try {
      await this._playerRef.current?.play()
    } catch (error) {
      console.error('error on play method', error)
    }
  }

  recoverFromNetworkError() {
    try {
      // HLS.js recovery: restart loading from current position
      if (this._hls && this.isInitialized) {
        const currentTime = this._playerRef.current?.currentTime || 0
        this._hls.startLoad(currentTime)
        // Clear the network error flag on successful recovery
        this._hadNetworkError = false
        return true
      }
      return false
    } catch (error) {
      console.error('HLS: Recovery failed', error)
      return false
    }
  }

  pause() {
    this._playerRef.current?.pause()
  }

  seek(newCurrentTime) {
    this._playerRef.current.currentTime = newCurrentTime
  }

  isReady() {
    return this._playerRef.current?.readyState >= 3
  }

  destroy() {
    this.isInitialized = false
    this._hls.destroy()
  }

  // TODO: the 2 methods below are maybe static methods
  // eslint-disable-next-line class-methods-use-this
  getDOMImplementation() {
    return HTML5Player
  }

  // eslint-disable-next-line class-methods-use-this
  getVersion() {
    return Hls.version
  }
}
